import React, { useEffect, useId, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Eye, EyeOff } from 'lucide-react';
import { Banner, Button, Field, Switch, Tag } from '../kit';
import PickerMenu from './PickerMenu';
import { PASSWORD_RULES, REPORT_GROUPS, ROLES, isLocationRole, isValidPassword } from './userModel';
import { apiErrorMessage, resetPassword } from './usersApi';

/**
 * Generic dialog on the kit's dialog chrome, for dialogs whose footer is not
 * a plain Cancel/Confirm pair. Esc / scrim close unless `busy`.
 */
export function Dialog({ open, title, children, footer, onClose, busy, wide }) {
  const ref = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented || busy) return;
      if (document.querySelector('[data-radix-popper-content-wrapper]')) return;
      closeRef.current && closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy]);
  useEffect(() => {
    if (!open) return;
    const first = ref.current?.querySelector('input, select, button:not([disabled])');
    if (first) first.focus();
  }, [open]);
  if (!open) return null;
  return ReactDOM.createPortal(
    <div className="ak-layer ak-dialog-wrap" onClick={busy ? undefined : onClose}>
      <div
        ref={ref}
        className={`ak-dialog${wide ? ' ak-wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ak-dialog-body">
          <h2>{title}</h2>
          {children}
        </div>
        <div className="ak-dialog-foot">{footer}</div>
      </div>
    </div>,
    document.body
  );
}

export function CopyButton({ value, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      window.prompt('Copy the value:', value); // clipboard blocked (http / permissions)
    }
  };
  return <Button size="small" onClick={copy} aria-live="polite">{copied ? 'Copied' : label}</Button>;
}

export function SecretValue({ value, label }) {
  return (
    <div className="us-secret">
      <code className="ak-mono" aria-label={label}>{value}</code>
      <CopyButton value={value} />
    </div>
  );
}

/** Shows a temporary password that could not be emailed. */
const isInviteLink = (value) => /^https?:\/\/.+\/welcome#t=/.test(String(value || ''));

export function SecretDialog({ open, title, message, secret, onClose }) {
  return (
    <Dialog open={open} title={title} onClose={onClose} footer={<Button variant="primary" onClick={onClose}>Done</Button>}>
      {message ? <p>{message}</p> : null}
      {secret ? <SecretValue value={secret} label={isInviteLink(secret) ? 'Invite link' : 'Temporary password'} /> : null}
      <p className="ak-help">
        {isInviteLink(secret)
          ? 'Share it securely — it works once, for 72 hours, and lets them choose their own password.'
          : "Share it securely — they'll be asked to choose a new password when they sign in."}
      </p>
    </Dialog>
  );
}

export function PasswordResetDialog({ open, user, onClose }) {
  const [mode, setMode] = useState('generate');
  const [custom, setCustom] = useState('');
  const [show, setShow] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const inputId = useId();

  useEffect(() => {
    if (open) {
      setMode('generate'); setCustom(''); setShow(false); setSendEmail(true);
      setBusy(false); setResult(null); setError(null);
    }
  }, [open, user?.email]);

  if (!user) return null;
  const customInvalid = mode === 'custom' && !isValidPassword(custom);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await resetPassword(user.email, { customPassword: mode === 'custom' ? custom : null, sendEmail });
      const cognitoOk = res?.results?.cognito?.success !== false;
      if (!res?.success || !cognitoOk) {
        setError(res?.results?.cognito?.error || res?.message || 'Password reset failed.');
      } else {
        setResult({
          password: res.results?.temporaryPassword || null,
          custom: mode === 'custom',
          emailed: sendEmail && res.results?.email?.success !== false,
          emailFailed: sendEmail && res.results?.email?.success === false,
          emailError: res.results?.email?.error,
        });
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'Password reset failed.'));
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <Dialog open={open} title="Password reset" onClose={onClose} footer={<Button variant="primary" onClick={onClose}>Done</Button>}>
        <p>
          {user.firstName || user.email} will be asked to choose a new password the next time they sign in.
          {result.emailed ? ` We emailed the temporary password to ${user.email}.` : ''}
        </p>
        {result.emailFailed ? (
          <Banner tone="warn" title="The email didn't send">{result.emailError || 'Share the password another way.'}</Banner>
        ) : null}
        {result.password ? (
          <Field label="Temporary password">
            <SecretValue value={result.password} label="Temporary password" />
          </Field>
        ) : result.custom ? <p className="ak-help">The custom password you entered is now their temporary password.</p> : null}
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      title="Reset password"
      onClose={onClose}
      busy={busy}
      footer={(
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={busy || customInvalid}>{busy ? 'Resetting…' : 'Reset password'}</Button>
        </>
      )}
    >
      <p>Sets a temporary password for <strong>{user.email}</strong>. They'll choose a new one when they next sign in.</p>
      <div className="us-radio-row" role="radiogroup" aria-label="Password">
        <label><input type="radio" name="pw-mode" checked={mode === 'generate'} onChange={() => setMode('generate')} /> Generate a password</label>
        <label><input type="radio" name="pw-mode" checked={mode === 'custom'} onChange={() => setMode('custom')} /> Set a custom password</label>
      </div>
      {mode === 'custom' ? (
        <Field label="Custom password" htmlFor={inputId}>
          <div className="us-input-affix">
            <input
              id={inputId}
              className="ak-input"
              type={show ? 'text' : 'password'}
              value={custom}
              autoComplete="new-password"
              aria-invalid={custom && customInvalid ? 'true' : undefined}
              onChange={(e) => setCustom(e.target.value)}
            />
            <button type="button" className="ak-btn ak-ghost ak-icon ak-small" aria-label={show ? 'Hide password' : 'Show password'} onClick={() => setShow((s) => !s)}>
              {show ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
            </button>
          </div>
          <ul className="us-rules">
            {PASSWORD_RULES.map((r) => (
              <li key={r.key} className={r.test(custom) ? 'us-ok' : undefined}>{r.label}</li>
            ))}
          </ul>
        </Field>
      ) : null}
      <label className="us-check-row">
        <input type="checkbox" className="ak-check" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
        Email the temporary password to {user.email}
      </label>
      {error ? <Banner tone="bad" title="Couldn't reset the password">{error}</Banner> : null}
    </Dialog>
  );
}

/** Bulk: add the selected users to one organization with a role. */
export function AddToOrgDialog({ open, count, orgs, locationsForOrg, busy, onSubmit, onClose }) {
  const [orgCode, setOrgCode] = useState('');
  const [role, setRole] = useState('orgViewer');
  const [locationIds, setLocationIds] = useState([]);
  const orgId = useId();
  const roleId = useId();
  useEffect(() => { if (open) { setOrgCode(orgs.length === 1 ? orgs[0].code : ''); setRole('orgViewer'); setLocationIds([]); } }, [open, orgs]);
  const locations = orgCode ? locationsForOrg(orgCode) : [];
  const needsLocations = isLocationRole(role);
  const valid = orgCode && role && (!needsLocations || locationIds.length > 0);
  return (
    <Dialog
      open={open}
      title={`Add ${count} user${count === 1 ? '' : 's'} to an organization`}
      onClose={onClose}
      busy={busy}
      footer={(
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" disabled={!valid || busy} onClick={() => onSubmit({ orgCode, role, locationIds: needsLocations ? locationIds : [] })}>
            {busy ? 'Adding…' : 'Add to organization'}
          </Button>
        </>
      )}
    >
      <p>Users already in the organization get this role instead. Report access for new members starts empty — set it next.</p>
      <Field label="Organization" htmlFor={orgId}>
        <select id={orgId} className="ak-select" value={orgCode} onChange={(e) => { setOrgCode(e.target.value); setLocationIds([]); }}>
          <option value="">Choose an organization</option>
          {orgs.map((o) => <option key={o.code} value={o.code}>{o.name}</option>)}
        </select>
      </Field>
      <Field label="Role" htmlFor={roleId} help={ROLES.find((r) => r.value === role)?.help}>
        <select id={roleId} className="ak-select" value={role} onChange={(e) => setRole(e.target.value)}>
          {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </Field>
      {needsLocations ? (
        <div className="ak-field">
          <span className="ak-label">Locations</span>
          <div className="ak-tags">
            {locationIds.map((id) => {
              const loc = locations.find((l) => String(l.id) === String(id));
              return <Tag key={id} onRemove={() => setLocationIds((l) => l.filter((x) => x !== id))}>{loc?.name || `Location ${id}`}</Tag>;
            })}
            <PickerMenu
              triggerClassName="ak-btn ak-ghost ak-small"
              disabled={!orgCode}
              searchPlaceholder="Search locations"
              options={locations.filter((l) => !locationIds.some((x) => String(x) === String(l.id))).map((l) => ({ value: l.id, label: l.name }))}
              onPick={(id) => setLocationIds((l) => [...l, id])}
            >
              + Add location
            </PickerMenu>
          </div>
          {!locationIds.length ? <span className="ak-help">Pick at least one location for a location role.</span> : null}
        </div>
      ) : null}
    </Dialog>
  );
}

/** Bulk: set the report groups the selected users see in one organization. */
export function SetReportAccessDialog({ open, count, orgs, busy, onSubmit, onClose }) {
  const [orgCode, setOrgCode] = useState('');
  const [groups, setGroups] = useState([]);
  const orgId = useId();
  useEffect(() => { if (open) { setOrgCode(orgs.length === 1 ? orgs[0].code : ''); setGroups([]); } }, [open, orgs]);
  const toggle = (g) => setGroups((cur) => (cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g]));
  return (
    <Dialog
      open={open}
      title={`Set report access for ${count} user${count === 1 ? '' : 's'}`}
      onClose={onClose}
      busy={busy}
      footer={(
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" disabled={!orgCode || busy} onClick={() => onSubmit({ orgCode, reportGroups: groups })}>
            {busy ? 'Saving…' : 'Set report access'}
          </Button>
        </>
      )}
    >
      <p>Replaces their report access in the chosen organization. Users who aren't members are skipped.</p>
      <Field label="Organization" htmlFor={orgId}>
        <select id={orgId} className="ak-select" value={orgCode} onChange={(e) => setOrgCode(e.target.value)}>
          <option value="">Choose an organization</option>
          {orgs.map((o) => <option key={o.code} value={o.code}>{o.name}</option>)}
        </select>
      </Field>
      <div className="ak-field">
        <span className="ak-label">Report access</span>
        <div className="us-switch-grid">
          {REPORT_GROUPS.map((g) => (
            <Switch key={g.value} label={g.label} title={g.blurb} checked={groups.includes(g.value)} onChange={() => toggle(g.value)} />
          ))}
        </div>
      </div>
    </Dialog>
  );
}

const ACTION_NOUN = {
  addToOrg: 'Add to organization',
  setReportAccess: 'Set report access',
  resendInvite: 'Resend invites',
  deactivate: 'Deactivate',
};

/** Per-user outcome of a bulk action (only opened when something failed or was skipped). */
export function BulkResultDialog({ result, onClose }) {
  const problems = (result?.results || []).filter((r) => !r.ok);
  return (
    <Dialog
      open={Boolean(result)}
      title={`${ACTION_NOUN[result?.action] || 'Bulk action'}: ${result?.succeeded || 0} done, ${problems.length} not`}
      onClose={onClose}
      wide
      footer={<Button variant="primary" onClick={onClose}>Done</Button>}
    >
      <table className="ak-tbl us-result-tbl">
        <thead><tr><th>User</th><th>Result</th></tr></thead>
        <tbody>
          {problems.map((r) => (
            <tr key={r.id}>
              <td>{r.email || r.id}</td>
              <td><span className={r.status === 'failed' ? 'us-fail' : 'ak-muted'}>{r.status === 'failed' ? 'Failed' : 'Skipped'}</span> — {r.message}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Dialog>
  );
}
